import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { channelService } from '@/services/channels';
import { channelMutations } from '@/services/channels/mutations';
import type { 
  Channel, 
  CreateChannelInput, 
  UpdateChannelInput, 
  UpdateChannelData,
  BulkUpdateChannelsInput
} from '@/features/channels/types'; 
import { supabase } from '@/lib/supabase';


interface DeleteChannelsParams {
  ids: number[];
  providerId: number;
  versionId: number;
}

export function useChannels(providerId: number, versionId: number) {
  return useQuery({
    queryKey: ['channels', providerId, versionId],
    queryFn: () => channelService.getChannels(providerId, versionId)
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();

  return useMutation<Channel, Error, CreateChannelInput>({
    mutationFn: channelService.createChannel,
    onSuccess: (_, variables) => {
      // Invalidate queries with broad prefix match to cover all variations
      queryClient.invalidateQueries({
        queryKey: ['channels', variables.providerId, variables.versionId]
      });
    }
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();

  return useMutation<Channel, Error, UpdateChannelInput, { previousChannels?: Channel[] }>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return channelService.updateChannel(id, rest as UpdateChannelData);
    },
    onSuccess: (updatedChannel, variables) => {
      console.log('[DEBUG] onSuccess started', { channelId: updatedChannel.id });

      // Directly update the cache with the real server response instead of invalidating
      // This avoids the expensive refetch of all 383 channels
      queryClient.setQueriesData<Channel[]>(
        { queryKey: ['channels', variables.providerId, variables.versionId] },
        (oldData) => {
          console.log('[DEBUG] setQueriesData callback called', { hasOldData: !!oldData });
          if (!oldData) return oldData;

          // Replace the optimistic update with real server data
          const newData = oldData.map(channel =>
            channel.id === updatedChannel.id ? updatedChannel : channel
          );
          console.log('[DEBUG] Cache updated', { channelCount: newData.length });
          return newData;
        }
      );

      // Check query state after cache update
      const queries = queryClient.getQueriesData<Channel[]>({
        queryKey: ['channels', variables.providerId, variables.versionId]
      });
      console.log('[DEBUG] Queries after cache update:', queries.length);

      queries.forEach(([key, data]) => {
        const queryState = queryClient.getQueryState(key) as any;
        console.log('[DEBUG] Query state:', {
          key,
          dataLength: data?.length,
          isFetching: queryState?.isFetching,
          isLoading: queryState?.isLoading,
          status: queryState?.status,
          fetchStatus: queryState?.fetchStatus
        });
      });

      // No need to invalidate - we already have the latest data from the server!
      console.log('[DEBUG] onSuccess completed');
    }
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { id: number; providerId: number; versionId: number }>({
    mutationFn: ({ id }) => channelService.deleteChannel(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['channels', variables.providerId, variables.versionId] 
      });
    }
  });
}

export function useDeleteChannels() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, DeleteChannelsParams>({
    mutationFn: async ({ ids }) => {
      await Promise.all(ids.map(id => channelService.deleteChannel(id)));
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['channels', variables.providerId, variables.versionId] 
      });
      toast.success("Channels Deleted", {
        description: `${variables.ids.length} channels deleted successfully`,
        duration: 3000,
      });
    },
    onError: (error) => {
      toast.error("Delete Failed", {
        description: error instanceof Error ? error.message : 'Failed to delete channels',
        duration: 4000,
      });
    }
  });
}

export function useBulkUpdateChannels() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { updates: UpdateChannelInput[]; providerId: number; versionId: number }>({
    mutationFn: ({ updates, providerId, versionId }) => {
      // Extract the first update to use as a base
      const firstUpdate = updates[0];
      
      // Build the update object, being careful to include only the properties that need updating
      const updateData: BulkUpdateChannelsInput = {
        channelIds: updates.map(update => update.id),
        providerId,
        versionId
      };
      
      // Only include category updates if they are defined
      if (firstUpdate.categoryIds !== undefined) {
        updateData.category_ids = firstUpdate.categoryIds;
        updateData.default_category_id = firstUpdate.defaultCategoryId;
        updateData.categoryUpdateMode = firstUpdate.categoryUpdateMode || 'replace';
      }
      
      // Only include plan updates if they are defined
      if (firstUpdate.planIds !== undefined) {
        updateData.plan_ids = firstUpdate.planIds;
      }
      
      // Only include description if it's being updated
      if (firstUpdate.description !== undefined) {
        updateData.description = firstUpdate.description === null ? undefined : firstUpdate.description;
      }
      
      // Only include price if it's being updated
      if (firstUpdate.price !== undefined) {
        updateData.price = firstUpdate.price;
      }
      
      // Ensure is_local is properly passed if it's being updated
      if (firstUpdate.is_local !== undefined) {
        updateData.is_local = firstUpdate.is_local;
        console.log('[useChannels] Including is_local in bulk update:', firstUpdate.is_local);
      }
      
      // Ensure is_duplicate is properly passed if it's being updated
      if (firstUpdate.is_duplicate !== undefined) {
        updateData.is_duplicate = firstUpdate.is_duplicate;
        console.log('[useChannels] Including is_duplicate in bulk update:', firstUpdate.is_duplicate);
      }
      
      console.log('[useChannels] Bulk update data:', updateData);
      
      // Pass the properly constructed update object to the mutation
      return channelMutations.bulkUpdateChannels(updateData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['channels', variables.providerId, variables.versionId] 
      });
      toast.success("Bulk Update Complete", {
        description: "Channels updated successfully",
        duration: 3000,
      });
    },
    onError: (error) => {
      console.error('[useChannels] Bulk update error:', error);
      toast.error("Bulk Update Failed", {
        description: error instanceof Error ? error.message : 'Failed to update channels',
        duration: 4000,
      });
    }
  });
}

export function useNextSequenceNumber(providerId: number, versionId: number) {
  // First, check if provider supports sequences
  const { data: provider } = useQuery({
    queryKey: ['provider-sequence-support', providerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('has_sequence_numbers')
        .eq('id', providerId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!providerId,
    staleTime: Infinity, // This won't change during session
    gcTime: Infinity,
  });

  // Only fetch sequence number if provider supports it
  return useQuery({
    queryKey: ['next-sequence-number', providerId, versionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_next_sequence_number', {
          p_provider_id: providerId,
          p_version_id: versionId
        });

      if (error) {
        // Don't log error for expected case of non-sequential provider
        if (error.message !== 'Provider does not support sequence numbers') {
          console.error('Failed to get next sequence number:', error);
        }
        return null;
      }
      return data;
    },
    enabled: !!providerId && !!versionId && provider?.has_sequence_numbers === true,
  });
}

export function useFixSequentialNumbers() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ providerId, versionId }: { providerId: number, versionId: number }) => {
      return channelService.fixSequentialNumbers(providerId, versionId);
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ 
        queryKey: ['channels', variables.providerId, variables.versionId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['next-sequence-number', variables.providerId, variables.versionId] 
      });
      
      toast.success('Sequential numbers have been fixed');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to fix sequential numbers');
    }
  });
}

export function useCopyChannelsBetweenRegions() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      sourceVersionId,
      targetVersionId,
      copyLocal,
      specificChannelIds,
      // @ts-expect-error TS6133 - providerId is used in onSuccess callback
      providerId
    }: {
      sourceVersionId: number;
      targetVersionId: number;
      copyLocal: boolean;
      specificChannelIds?: number[];
      providerId: number;
    }) => {
      return channelService.copyChannelsBetweenRegions(
        sourceVersionId,
        targetVersionId,
        copyLocal,
        specificChannelIds
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate queries for both source and target versions
      queryClient.invalidateQueries({ 
        queryKey: ['channels', variables.providerId, variables.targetVersionId] 
      });
      
      // Also invalidate categories and plans
      queryClient.invalidateQueries({
        queryKey: ['categories', variables.providerId, variables.targetVersionId]
      });
      
      queryClient.invalidateQueries({
        queryKey: ['plans', variables.providerId, variables.targetVersionId]
      });
      
      // Invalidate channel types for the target version
      queryClient.invalidateQueries({
        queryKey: ['channel-types', variables.providerId, variables.targetVersionId]
      });
      
      toast.success('Channels copied successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'An unexpected error occurred');
    }
  });
}
